/**
 * QA Score Readiness Panel
 *
 * Surfaces the C.R.A.F.T. proposal-readiness score for a report.
 *
 * Two paths share one endpoint (POST /reports/{report_id}/evaluate),
 * exposed via {@link scoreReadiness} in `qaApi`:
 *
 *  1. "Score this proposal" — evaluates the persisted v2 (or v1) report
 *     content against the persisted MOTI rubric (legacy zero-arg form).
 *  2. "Score an external file" — uploads an arbitrary proposal file
 *     (PDF / DOCX / MD / TXT), with an optional custom rubric (JSON file
 *     or inline JSON) and an optional readiness target percentage.
 *
 * The panel renders, for either mode:
 *  - The big readiness number (`overall_readiness_pct`) with a target-line
 *    marker at `target_score_pct * 100`.
 *  - One row per `criterion_scores[i]` with a bar, score / max, and the
 *    rationale as a tooltip.
 *  - The top high-impact fixes (`top_high_impact_fixes`, up to 5) with
 *    severity badges, section reference, description, recommendation, and
 *    the estimated points recoverable.
 *  - A footer with the evaluated-at timestamp and `evaluation_source`
 *    ("craft" vs "craft_custom_rubric").
 *
 * Mounted in `ReportViewer.tsx` (QA tab, after Pre-Submission) and in
 * `ReviewDashboard.tsx` so reviewers see readiness as part of the review
 * flow. Must not be moved into `tracekit/packages/apps/keystone-rfp/` —
 * shared QA UI lives in `@tracekit/core`.
 */

import React, { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { Input } from '../UI/Input';
import { LoadingSpinner } from '../UI/LoadingSpinner';
import {
  AlertTriangle,
  CheckCircle,
  ClipboardList,
  FileUp,
  Gauge,
  ListChecks,
  RefreshCw,
  Target,
  TrendingUp,
} from 'lucide-react';
import {
  buildDetailedScoringMatrix,
  scoreReadiness,
  type HighImpactFix,
  type ProposalEvaluationResponse,
  type ScoringMatrixCriterionRow,
} from '../../api/qaApi';
import { SelectionCorrectionsPanel } from './SelectionCorrectionsPanel';

export interface QAScoreReadinessPanelProps {
  reportId: string;
  /**
   * Latest executive summary version from report metadata (e.g. v5). Shown so users know
   * which markdown \"Score this proposal\" will feed to the rubric after regeneration.
   */
  executiveSummaryVersionLabel?: string;
  /**
   * Shown beside Re-score when the latest run is below the target readiness
   * threshold. Opens the feedback-plan modal — Step&nbsp;1 auto-computes the plan.
   */
  onBelowTargetFeedbackStep1?: () => void;
  /**
   * Step&nbsp;2: jump to prompt preview / approve flow (plan must already exist
   * in the modal cache or user recomputes there).
   */
  onBelowTargetFeedbackStep2?: () => void;
  /**
   * Pre-loaded evaluation result. When provided, the panel renders the results
   * immediately without requiring the user to click "Score Proposal". Re-score
   * button still available to refresh.
   */
  initialData?: ProposalEvaluationResponse;
}

/**
 * Keystone procurement bar lands in the 60-70% band — stays aligned with the backend
 * default ``DEFAULT_TARGET_SCORE_PCT`` in ``proposals/evaluation/service.py``.
 * Also used as the pre-flight default in the external upload form.
 */
const DEFAULT_TARGET_SCORE_PCT = 0.60;

type Mode = 'idle' | 'external';

interface ExternalForm {
  proposalFile: File | null;
  criteriaFile: File | null;
  targetScorePct: number;
}

type EvaluationArgs =
  | { kind: 'persisted'; forceRefresh?: boolean }
  | {
      kind: 'external';
      proposalFile?: File;
      criteriaFile?: File;
      targetScorePct?: number;
      forceRefresh?: boolean;
    };

/** Severity → token-aware classes for the high-impact-fix badge. */
function severityClasses(severity: HighImpactFix['severity']): string {
  switch (severity) {
    case 'critical':
      return 'bg-red-100 text-red-800 border border-red-200 dark:bg-red-900/30 dark:text-red-200 dark:border-red-800';
    case 'major':
      return 'bg-amber-100 text-amber-800 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-800';
    case 'minor':
    default:
      return 'bg-gray-100 text-gray-700 border border-gray-200 dark:bg-gray-800/60 dark:text-gray-300 dark:border-gray-700';
  }
}

function formatPct(value: number | undefined | null, digits = 1): string {
  if (value == null || Number.isNaN(value)) return '—';
  return `${(value * 100).toFixed(digits)}%`;
}

function alignmentBadgeClasses(alignment: ScoringMatrixCriterionRow['alignment']): string {
  switch (alignment) {
    case 'Strong':
      return 'bg-green-100 text-green-800 border border-green-200 dark:bg-green-900/30 dark:text-green-200 dark:border-green-800';
    case 'Partial':
      return 'bg-amber-100 text-amber-800 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-800';
    case 'Poor':
    default:
      return 'bg-red-100 text-red-800 border border-red-200 dark:bg-red-900/30 dark:text-red-200 dark:border-red-800';
  }
}

export function QAScoreReadinessPanel({
  reportId,
  executiveSummaryVersionLabel,
  onBelowTargetFeedbackStep1,
  onBelowTargetFeedbackStep2,
  initialData,
}: QAScoreReadinessPanelProps) {
  const [mode, setMode] = useState<Mode>('idle');
  const [externalForm, setExternalForm] = useState<ExternalForm>({
    proposalFile: null,
    criteriaFile: null,
    targetScorePct: DEFAULT_TARGET_SCORE_PCT,
  });
  // Seed with pre-loaded data if provided (e.g. from Report Content tab "Score" button).
  const [preloaded, setPreloaded] = useState<ProposalEvaluationResponse | undefined>(initialData);

  const evaluation = useMutation<ProposalEvaluationResponse, Error, EvaluationArgs>({
    mutationFn: async (args) => {
      if (args.kind === 'external') {
        return scoreReadiness({
          reportId,
          proposalFile: args.proposalFile,
          criteriaFile: args.criteriaFile,
          targetScorePct: args.targetScorePct,
          forceRefresh: args.forceRefresh,
        });
      }
      return scoreReadiness({ reportId, forceRefresh: args.forceRefresh });
    },
    onSuccess: () => {
      // Clear preloaded once we have a fresh result.
      setPreloaded(undefined);
    },
  });

  const data = evaluation.data ?? preloaded;
  const isFlight = evaluation.isPending;
  const error = evaluation.error;

  const targetPct = useMemo(() => {
    if (data?.target_score_pct != null) return data.target_score_pct;
    if (mode === 'external') return externalForm.targetScorePct;
    return DEFAULT_TARGET_SCORE_PCT;
  }, [data?.target_score_pct, mode, externalForm.targetScorePct]);

  const readinessPct = data ? Math.max(0, Math.min(1, data.overall_readiness_pct)) : 0;
  const readinessHitsTarget = data ? data.overall_readiness_pct >= targetPct : false;
  // Show plan buttons whenever there is scored data and the callbacks are wired in —
  // users may want to push above target too, not only recover from below.
  const showFeedbackShortcuts = Boolean(
    data && onBelowTargetFeedbackStep1 && onBelowTargetFeedbackStep2,
  );

  const scoringMatrix = useMemo(
    () => (data ? buildDetailedScoringMatrix(data) : null),
    [data],
  );

  const handleScoreThisProposal = () => {
    setMode('idle');
    evaluation.mutate({ kind: 'persisted' });
  };

  const handleOpenExternal = () => {
    setMode('external');
    evaluation.reset();
  };

  const handleSubmitExternal = (e: React.FormEvent) => {
    e.preventDefault();
    evaluation.mutate({
      kind: 'external',
      proposalFile: externalForm.proposalFile ?? undefined,
      criteriaFile: externalForm.criteriaFile ?? undefined,
      targetScorePct: externalForm.targetScorePct,
    });
  };

  const handleRetry = () => {
    if (mode === 'external') {
      evaluation.mutate({
        kind: 'external',
        proposalFile: externalForm.proposalFile ?? undefined,
        criteriaFile: externalForm.criteriaFile ?? undefined,
        targetScorePct: externalForm.targetScorePct,
        forceRefresh: true,
      });
    } else {
      evaluation.mutate({ kind: 'persisted', forceRefresh: true });
    }
  };

  return (
    <Card>
      {/* Header row — title + description only (no buttons here to avoid wrap-hiding) */}
      <div className="mb-4 flex items-start gap-3">
        <div className="p-2 rounded-lg bg-primary-50 dark:bg-primary-900/20 shrink-0">
          <Gauge className="w-5 h-5 text-primary-600 dark:text-primary-300" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-theme-text">Score Readiness</h3>
          <p className="text-sm text-theme-text-secondary">
            Evaluate this proposal — or an external file — against the technical rubric.
          </p>
          <p className="mt-1 text-xs text-amber-800/90 dark:text-amber-200/85">
            {executiveSummaryVersionLabel ? (
              <>
                <span className="font-medium">Score this proposal</span> reads the saved
                executive summary matching this report&apos;s latest version (
                <span className="font-mono">{executiveSummaryVersionLabel}</span>) — same
                idea as the Report Content tab. Rubric numbers only change after you click
                Score / Re-score; formatting and grammar checks must be re-run separately.
              </>
            ) : (
              <>
                Rubric scores update only when you run Score / Re-score. After regenerating
                content, open Report Content to confirm the version, then re-score here.
              </>
            )}
          </p>
        </div>
      </div>

      {/* Action buttons — own full-width row so they never get clipped by flex-wrap */}
      {data && !isFlight && (
        <div className="mb-6 flex flex-wrap items-center gap-2">
          {showFeedbackShortcuts && (
            <>
              <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<ListChecks className="w-4 h-4" />}
                  onClick={onBelowTargetFeedbackStep1}
                  title="Bucket gaps, comments, and revision notes into a regen plan (auto-build)"
                >
                  1 · QA regen plan
                </Button>
              <Button
                variant="outline"
                size="sm"
                leftIcon={<ClipboardList className="w-4 h-4" />}
                onClick={onBelowTargetFeedbackStep2}
                title="Expand prompt preview, then approve regeneration"
              >
                2 · Prompt &amp; approve
              </Button>
            </>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleRetry}
            leftIcon={<RefreshCw className="w-4 h-4" />}
          >
            Re-score
          </Button>
        </div>
      )}

      {!data && !isFlight && !error && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={handleScoreThisProposal}
              leftIcon={<TrendingUp className="w-4 h-4" />}
            >
              Score this proposal
            </Button>
            <Button
              variant="outline"
              onClick={handleOpenExternal}
              leftIcon={<FileUp className="w-4 h-4" />}
            >
              Score an external file
            </Button>
          </div>

          {mode === 'external' && (
            <form
              onSubmit={handleSubmitExternal}
              className="p-4 bg-theme-surface rounded-lg border border-theme-border space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-theme-text mb-1">
                  Proposal file (PDF / DOCX / MD / TXT)
                </label>
                <input
                  type="file"
                  accept=".pdf,.docx,.md,.txt"
                  data-testid="proposal-file-input"
                  className="block w-full text-sm text-theme-text"
                  onChange={(e) =>
                    setExternalForm((s) => ({
                      ...s,
                      proposalFile: e.target.files?.[0] ?? null,
                    }))
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-theme-text mb-1">
                  Custom rubric (JSON, optional)
                </label>
                <input
                  type="file"
                  accept="application/json,.json"
                  data-testid="criteria-file-input"
                  className="block w-full text-sm text-theme-text"
                  onChange={(e) =>
                    setExternalForm((s) => ({
                      ...s,
                      criteriaFile: e.target.files?.[0] ?? null,
                    }))
                  }
                />
              </div>
              <div className="max-w-xs">
                <Input
                  label="Target score (0..1)"
                  type="number"
                  step="0.01"
                  min={0}
                  max={1}
                  value={externalForm.targetScorePct}
                  onChange={(e) =>
                    setExternalForm((s) => ({
                      ...s,
                      targetScorePct: Number(e.target.value),
                    }))
                  }
                  helperText={`Default ${DEFAULT_TARGET_SCORE_PCT}`}
                  data-testid="target-score-input"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  type="submit"
                  disabled={!externalForm.proposalFile}
                  leftIcon={<TrendingUp className="w-4 h-4" />}
                >
                  Evaluate file
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setMode('idle');
                    setExternalForm({
                      proposalFile: null,
                      criteriaFile: null,
                      targetScorePct: DEFAULT_TARGET_SCORE_PCT,
                    });
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </div>
      )}

      {isFlight && (
        <div className="flex items-center justify-center py-12" role="status">
          <LoadingSpinner />
          <span className="ml-3 text-theme-text-secondary">Evaluating against rubric…</span>
        </div>
      )}

      {error && !isFlight && (
        <div className="text-center py-8">
          <AlertTriangle className="h-12 w-12 mx-auto text-red-400 mb-4" />
          <p className="text-red-600 dark:text-red-400 mb-4">
            Evaluation failed: {error instanceof Error ? error.message : 'Unknown error'}
          </p>
          <Button onClick={handleRetry} variant="outline">
            Retry
          </Button>
        </div>
      )}

      {data && !isFlight && (
        <div className="space-y-6">
          {/* Inferred rubric notice — shown when the doc had criteria but no explicit point values */}
          {data.evaluation_source === 'craft_inferred' && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <p className="text-xs leading-snug">
                <span className="font-semibold">Approximate score</span> — the uploaded RFP
                documents contained evaluation criteria but no explicit point values. Weights have
                been inferred proportionally from the document content. Results are indicative only.
              </p>
            </div>
          )}

          {/* Big readiness number with target-line marker */}
          <div
            className={
              'p-5 rounded-lg border-2 ' +
              (readinessHitsTarget
                ? 'border-emerald-300 bg-emerald-50/80 dark:border-emerald-700 dark:bg-emerald-950/30'
                : 'border-amber-300 bg-amber-50/80 dark:border-amber-700 dark:bg-amber-950/30')
            }
          >
            <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-theme-text-secondary mb-1">
                  Overall readiness
                </p>
                <div className="flex items-baseline gap-3">
                  <span
                    className={
                      'text-4xl font-bold ' +
                      (readinessHitsTarget
                        ? 'text-emerald-700 dark:text-emerald-300'
                        : 'text-amber-700 dark:text-amber-300')
                    }
                    data-testid="readiness-pct"
                  >
                    {formatPct(data.overall_readiness_pct)}
                  </span>
                  <span className="text-sm text-theme-text-secondary">
                    {data.total_score.toFixed(1)} / {data.max_total} pts
                  </span>
                </div>
              </div>
              <div className="text-right">
                <span
                  className={
                    'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ' +
                    (readinessHitsTarget
                      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                      : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200')
                  }
                >
                  {readinessHitsTarget ? 'On target' : 'Below target'}
                </span>
                <p className="mt-1 text-xs text-theme-text-secondary flex items-center gap-1 justify-end">
                  <Target className="w-3 h-3" /> Target {formatPct(targetPct, 0)}
                </p>
                {data.gap_to_target_points > 0 && (
                  <p className="mt-1 text-xs text-theme-text-secondary">
                    {data.gap_to_target_points.toFixed(1)} pts to target
                  </p>
                )}
              </div>
            </div>

            {/* Target-line marker on a horizontal bar */}
            <div className="relative h-3 w-full rounded-full bg-theme-border/40 overflow-hidden">
              <div
                className={
                  'h-full ' +
                  (readinessHitsTarget
                    ? 'bg-success-main dark:bg-success-light'
                    : 'bg-primary-500 dark:bg-primary-400')
                }
                style={{ width: `${readinessPct * 100}%` }}
              />
              <div
                aria-label="target marker"
                className="absolute top-[-2px] bottom-[-2px] w-[2px] bg-amber-500 dark:bg-amber-300"
                style={{ left: `${Math.max(0, Math.min(1, targetPct)) * 100}%` }}
              />
            </div>
            {showFeedbackShortcuts && (
              <p className="mt-3 border-t border-theme-border pt-3 text-xs leading-relaxed text-theme-text-secondary">
                Next: use{' '}
                <span className="font-medium text-theme-text">1 · QA regen plan</span> and{' '}
                <span className="font-medium text-theme-text">2 · Prompt &amp; approve</span>{' '}
                beside Re-score to review buckets, preview the exact prompt tips, then approve
                regeneration.
              </p>
            )}
          </div>

          {/* Criterion scoring matrix */}
          {scoringMatrix && scoringMatrix.criteria.length > 0 && (
            <div data-testid="criterion-scoring-matrix">
              {scoringMatrix.critical_missing_sections.length > 0 && (
                <div className="mb-3 flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2.5 text-red-800 dark:border-red-700 dark:bg-red-900/20 dark:text-red-200">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <p className="text-xs leading-snug">
                    <span className="font-semibold">Critical missing sections: </span>
                    {scoringMatrix.critical_missing_sections.join(', ')}
                  </p>
                </div>
              )}
              <h4 className="text-sm font-semibold text-theme-text mb-3">
                Criterion scoring matrix
              </h4>
              <div className="overflow-x-auto rounded-lg border border-theme-border">
                <table className="w-full table-fixed text-sm">
                  <colgroup>
                    <col className="w-[22%]" />
                    <col className="w-[7%]" />
                    <col className="w-[11%]" />
                    <col className="w-[9%]" />
                    <col className="w-[51%]" />
                  </colgroup>
                  <thead className="bg-gray-50 dark:bg-gray-900/40 text-left text-xs uppercase tracking-wide text-theme-text-secondary">
                    <tr>
                      <th className="px-3 py-2 font-semibold">RFP criterion</th>
                      <th className="px-3 py-2 font-semibold">Ref</th>
                      <th className="px-3 py-2 font-semibold">Alignment</th>
                      <th className="px-3 py-2 font-semibold">Score</th>
                      <th className="px-3 py-2 font-semibold">Notes &amp; gaps</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scoringMatrix.criteria.map((row) => (
                      <tr
                        key={row.rfp_reference}
                        className="border-t border-theme-border"
                        data-testid={`matrix-row-${row.rfp_reference}`}
                      >
                        <td className="px-3 py-2 text-theme-text align-top text-xs leading-snug">
                          {row.label}
                        </td>
                        <td className="px-3 py-2 text-theme-text-secondary align-top whitespace-nowrap text-xs">
                          {row.rfp_reference}
                        </td>
                        <td className="px-3 py-2 align-top">
                          <span
                            className={
                              'inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ' +
                              alignmentBadgeClasses(row.alignment)
                            }
                          >
                            {row.alignment}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-theme-text align-top whitespace-nowrap text-xs font-medium">
                          {row.score.toFixed(1)} / {row.max_score}
                        </td>
                        <td
                          className="px-3 py-2 text-xs text-theme-text-secondary align-top leading-snug line-clamp-3"
                          title={row.gap_analysis || undefined}
                        >
                          {row.gap_analysis || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <SelectionCorrectionsPanel
            reportId={reportId}
            onSaved={() => {
              if (!readinessHitsTarget && onBelowTargetFeedbackStep1) {
                onBelowTargetFeedbackStep1();
              }
            }}
            onOpenRegenPlan={onBelowTargetFeedbackStep1}
          />

          {data.selection_alignment &&
            ((data.selection_alignment.staff_mismatches?.length ?? 0) > 0 ||
              (data.selection_alignment.project_mismatches?.length ?? 0) > 0) && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-amber-900 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-200">
                <p className="text-xs font-semibold mb-1">Selection mismatches vs saved corrections</p>
                <ul className="text-xs space-y-1 list-disc pl-4">
                  {(data.selection_alignment.staff_mismatches ?? []).map((m, i) => (
                    <li key={`staff-m-${i}`}>
                      {String(m.expected_name ?? 'Staff')}: {String(m.issue ?? 'mismatch')}
                    </li>
                  ))}
                  {(data.selection_alignment.project_mismatches ?? []).map((m, i) => (
                    <li key={`proj-m-${i}`}>
                      {String(m.rejected_label ?? m.bucket_id ?? 'Project')}:{' '}
                      {String(m.issue ?? 'mismatch')}
                    </li>
                  ))}
                </ul>
              </div>
            )}

          {/* Per-criterion bars */}
          <div>
            <h4 className="text-sm font-semibold text-theme-text mb-3">Criterion scores</h4>
            <div className="space-y-3">
              {data.criterion_scores.map((c) => {
                const pct = c.max_points > 0 ? c.score / c.max_points : 0;
                return (
                  <div
                    key={c.criterion_id}
                    className="p-3 rounded-lg border border-theme-border"
                    data-testid={`criterion-row-${c.criterion_id}`}
                  >
                    <div className="flex items-center justify-between mb-2 gap-3">
                      <div
                        className="font-medium text-theme-text truncate"
                        title={c.rationale || c.title}
                      >
                        <span className="text-theme-text-secondary mr-2">{c.criterion_id}</span>
                        {c.title}
                      </div>
                      <div className="text-sm font-semibold text-theme-text whitespace-nowrap">
                        {c.score.toFixed(1)} / {c.max_points}
                      </div>
                    </div>
                    <div className="h-2 w-full rounded-full bg-theme-border/40 overflow-hidden">
                      <div
                        className="h-full bg-primary-500 dark:bg-primary-400"
                        style={{ width: `${Math.max(0, Math.min(1, pct)) * 100}%` }}
                      />
                    </div>
                    {c.rationale && (
                      <p className="mt-2 text-xs text-theme-text-secondary">{c.rationale}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Top high-impact fixes */}
          {data.top_high_impact_fixes && data.top_high_impact_fixes.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-theme-text mb-3">
                Top high-impact fixes
              </h4>
              <ul className="space-y-2" data-testid="high-impact-fixes">
                {data.top_high_impact_fixes.slice(0, 5).map((fix) => (
                  <li
                    key={fix.id}
                    className="p-3 rounded-lg border border-theme-border bg-theme-surface"
                  >
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
                      <div className="flex items-center gap-2">
                        <span
                          className={
                            'inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ' +
                            severityClasses(fix.severity)
                          }
                        >
                          {fix.severity}
                        </span>
                        <span className="text-xs text-theme-text-secondary">
                          {fix.section_reference}
                        </span>
                      </div>
                      <span className="text-xs font-semibold text-theme-text">
                        +{fix.estimated_points_recoverable.toFixed(1)} pts
                      </span>
                    </div>
                    <p className="text-sm text-theme-text">{fix.description}</p>
                    {fix.recommendation && (
                      <p className="mt-1 text-xs text-theme-text-secondary">
                        <span className="font-semibold">Recommendation: </span>
                        {fix.recommendation}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between flex-wrap gap-2 pt-3 border-t border-theme-border text-xs text-theme-text-secondary">
            <span className="inline-flex items-center gap-1">
              <CheckCircle className="w-3 h-3" /> Evaluated:{' '}
              {data.evaluated_at ? new Date(data.evaluated_at).toLocaleString() : '—'}
            </span>
            <span data-testid="evaluation-source">
              Source: <span className="font-medium">{data.evaluation_source}</span>
            </span>
          </div>
        </div>
      )}
    </Card>
  );
}

export default QAScoreReadinessPanel;
